import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  AllPointOfInterestsForDataLoaderDocument,
  PanelItemFieldTypeEnum,
  PoiStatus,
  SelectedPointOfInterestDocument,
  UpdatePointOfInterestInput,
  VisibilityEnum,
  useUpdatePoiGeneralFieldsMutation,
  useUpdatePoiTemplateFieldsMutation,
  useUpdatePointOfInterestMutation,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useTemplateFields } from '../../hooks/useTemplateFields';
import { useGeneralFields } from '../../hooks/useGeneralFields';
import { useBlisterData } from '../../hooks/useBlisterData';
import { useTriggerPoiEditing } from '../../hooks';
import * as poiState from '../../../../state';
import { TemplateFieldsForm } from '../TemplateFieldsForm';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import { StyledForm } from '../UpdateGeneralFields/styles';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { convertTemplateNameToPoiType, getPoiTemplateKey, convertTimeStampToDate } from '@/utils';
import { GeneralFieldsType } from '../../types';
import { CustomTextField, DropDownField } from '@/components/shared/CustomField';
import { CustomDateField } from '@/components/shared/CustomField/FieldTemplates/CustomDateField';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';

export const UpdateSectionFields = () => {
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const poi = editPointOfInterest?.pointOfInterest;
  const template = editPointOfInterest?.template;
  const section = editPointOfInterest?.editingSection;

  const navbarHeight = useNavbarHeight();
  const { showSnackBar } = useSnackBarMessage({});
  const { cancelEditing } = useTriggerPoiEditing();
  const [loading, setLoading] = useState(false);
  const { getPoiStatusLabel, getStatusOptions } = usePoiStatusLabels();
  const poiTypeForStatus = useMemo(
    () => template?.key ?? poi?.type,
    [template?.key, poi?.type]
  );
  const statusOptions = useMemo(
    () => (poiTypeForStatus ? getStatusOptions(poiTypeForStatus) : []),
    [getStatusOptions, poiTypeForStatus]
  );

  const poiType = convertTemplateNameToPoiType(getPoiTemplateKey(template));

  // Flatten all items from non-feature groups
  const allItems = (section?.groups ?? []).flatMap((group) =>
    group.feature ? [] : group.items
  );

  // Partition by fieldType
  const templateItems = allItems.filter(
    (item) => item.fieldType === PanelItemFieldTypeEnum.TemplateField
  );
  const generalItems = allItems.filter(
    (item) => item.fieldType === PanelItemFieldTypeEnum.PointOfInterest
  );
  const blisterItems = allItems.filter(
    (item) => item.fieldType === PanelItemFieldTypeEnum.Blister
  );

  // Template fields: filter template.fields to matching keys
  const sectionTemplateFields = (template?.fields ?? []).filter((field) =>
    templateItems.some((item) => item.key === field.key)
  );

  const templateDataHook = useTemplateFields({
    selectedPoiInfo: poi,
    templateFields: sectionTemplateFields,
  });

  const { localBlisterData } = useBlisterData(templateDataHook, poiType);

  // Build initial general state from only the keys present in generalItems
  const formattedDueDate = poi?.dueDate ? convertTimeStampToDate(poi.dueDate) : undefined;
  const initialGeneralState: GeneralFieldsType = {
    visibility: generalItems.some((i) => i.key === 'visibility')
      ? (poi?.visibility as GeneralFieldsType['visibility'])
      : undefined,
    dueDate: generalItems.some((i) => i.key === 'dueDate') ? formattedDueDate : undefined,
    status: generalItems.some((i) => i.key === 'status')
      ? ((poi?.status as PoiStatus) ?? undefined)
      : undefined,
    assignee: generalItems.some((i) => i.key === 'assignee')
      ? (poi?.assignee ?? undefined)
      : undefined,
    name: generalItems.some((i) => i.key === 'name') ? (poi?.name ?? undefined) : undefined,
    comment: generalItems.some((i) => i.key === 'comment')
      ? (poi?.comment ?? undefined)
      : undefined,
  };

  const generalFieldsData = useGeneralFields(initialGeneralState);

  const [mutateTemplateFields] = useUpdatePoiTemplateFieldsMutation();
  const [mutateUpdatePointOfInterest] = useUpdatePointOfInterestMutation();
  const [mutateGeneralFields] = useUpdatePoiGeneralFieldsMutation();

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      event.preventDefault();
      setLoading(true);

      const promises: Promise<unknown>[] = [];

      if (templateItems.length > 0 || blisterItems.length > 0) {
        const templateFieldData = Object.entries(templateDataHook.state).reduce<
          Record<string, string | number>
        >((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});

        if (poi?.blister && blisterItems.length > 0 && localBlisterData) {
          const blister = JSON.parse(
            JSON.stringify(localBlisterData),
            (key, value) => (key === '__typename' ? undefined : value)
          );
          promises.push(
            mutateUpdatePointOfInterest({
              variables: {
                input: {
                  pointOfInterestId: poi?.id ?? '',
                  blister,
                  templateId: template?.id,
                  type: poiType,
                  templateFieldData,
                  lastModifiedFor: `Update ${section?.label ?? 'Section'} Fields`,
                },
              },
              refetchQueries: [
                SelectedPointOfInterestDocument,
                AllPointOfInterestsForDataLoaderDocument,
              ],
            })
          );
        } else {
          promises.push(
            mutateTemplateFields({
              variables: {
                input: {
                  pointOfInterestId: poi?.id ?? '',
                  templateId: template?.id,
                  templateFieldData,
                  lastModifiedFor: `Update ${section?.label ?? 'Section'} Fields`,
                },
              },
              refetchQueries: [
                SelectedPointOfInterestDocument,
                AllPointOfInterestsForDataLoaderDocument,
              ],
            })
          );
        }
      }

      if (generalItems.length > 0) {
        const stringifiedDueDate = generalFieldsData.state.dueDate
          ? String(generalFieldsData.state.dueDate)
          : undefined;

        const generalInput: UpdatePointOfInterestInput = {
          pointOfInterestId: poi?.id ?? '',
          lastModifiedFor: `Update ${section?.label ?? 'Section'} Fields`,
          ...(generalItems.some((i) => i.key === 'name') && { name: generalFieldsData.state.name }),
          ...(generalItems.some((i) => i.key === 'status') && {
            status: generalFieldsData.state.status,
          }),
          ...(generalItems.some((i) => i.key === 'visibility') && {
            visibility: generalFieldsData.state.visibility,
          }),
          ...(generalItems.some((i) => i.key === 'assignee') && {
            assignee: generalFieldsData.state.assignee,
          }),
          ...(generalItems.some((i) => i.key === 'dueDate') && {
            dueDate: stringifiedDueDate,
          }),
          ...(generalItems.some((i) => i.key === 'comment') && {
            comment: generalFieldsData.state.comment,
          }),
        };

        promises.push(
          mutateGeneralFields({
            variables: { input: generalInput },
            refetchQueries: [
              SelectedPointOfInterestDocument,
              AllPointOfInterestsForDataLoaderDocument,
            ],
          })
        );
      }

      Promise.all(promises)
        .then(() => {
          showSnackBar('Point of interest updated', 'success');
          cancelEditing();
        })
        .catch(() => {
          showSnackBar('Could not update point of interest', 'error');
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      templateItems,
      blisterItems,
      generalItems,
      templateDataHook.state,
      poi?.id,
      poi?.blister,
      template?.id,
      poiType,
      localBlisterData,
      generalFieldsData.state,
      section?.label,
      mutateTemplateFields,
      mutateUpdatePointOfInterest,
      mutateGeneralFields,
      showSnackBar,
      cancelEditing,
    ]
  );

  return (
    <Box height="100%">
      <StyledForm onSubmit={handleSubmit}>
        <Stack
          sx={{
            height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`,
            overflowY: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box p={2} flex={1} mb="85px">
            <Typography variant="h5" sx={{ fontSize: '1.6rem', fontWeight: 500, mb: 2 }}>
              Edit {section?.label}
            </Typography>

            {templateItems.length > 0 && (
              <TemplateFieldsForm
                dataHook={templateDataHook}
                template={template}
                formState={editPointOfInterest?.formState}
                inputAdormantHandlerMap={{ MEASUREMENT_TOOL: () => {}, RELOAD: () => {}, '': () => {} }}
                handleChangeControlledField={() => {}}
              />
            )}

            {generalItems.map((item) => {
              const { updateValue } = generalFieldsData;
              switch (item.key) {
                case 'name': {
                  return (
                    <CustomTextField
                      key="name"
                      mode="Edit"
                      id="Name"
                      title="Name"
                      value={generalFieldsData.state.name}
                      sx={{ mt: 2 }}
                      onFieldValueChange={(value) => updateValue('name', value)}
                    />
                  );
                }
                case 'status': {
                  return (
                    <DropDownField
                      key="status"
                      mode="Edit"
                      id="Status"
                      title="Status"
                      options={statusOptions.map((opt) => opt.label)}
                      value={
                        generalFieldsData.state.status
                          ? getPoiStatusLabel(generalFieldsData.state.status, poiTypeForStatus)
                          : ''
                      }
                      sx={{ mt: 2 }}
                      onFieldValueChange={(value) => {
                        const selected = statusOptions.find((opt) => opt.label === value);
                        if (selected) updateValue('status', selected.value);
                      }}
                    />
                  );
                }
                case 'visibility': {
                  return (
                    <DropDownField
                      key="visibility"
                      mode="Edit"
                      id="Visibility"
                      title="Visibility"
                      options={Object.values(VisibilityEnum)}
                      value={generalFieldsData.state.visibility}
                      sx={{ mt: 2 }}
                      onFieldValueChange={(value) =>
                        updateValue('visibility', value as VisibilityEnum)
                      }
                    />
                  );
                }
                case 'assignee': {
                  return (
                    <CustomTextField
                      key="assignee"
                      mode="Edit"
                      id="Assignee"
                      title="Assignee"
                      value={generalFieldsData.state.assignee}
                      sx={{ mt: 2 }}
                      onFieldValueChange={(value) => updateValue('assignee', value)}
                    />
                  );
                }
                case 'dueDate': {
                  return (
                    <CustomDateField
                      key="dueDate"
                      format="dd MMM yyyy"
                      mode="Edit"
                      id="Due Date"
                      title="Due Date"
                      value={generalFieldsData.state.dueDate}
                      sx={{ mt: 2, mb: 0 }}
                      onFieldValueChange={(value) => updateValue('dueDate', value)}
                    />
                  );
                }
                case 'comment': {
                  return (
                    <CustomTextField
                      key="comment"
                      mode="Edit"
                      id="Comment"
                      title="Comment"
                      value={generalFieldsData.state.comment}
                      sx={{ mt: 2 }}
                      onFieldValueChange={(value) => updateValue('comment', value)}
                    />
                  );
                }
                default: {
                  return null;
                }
              }
            })}
          </Box>

          <SaveCancelFormButtons
            cancelButtonLabel="Cancel"
            buttonLabel="Save"
            loadingLabel="Updating..."
            onCancel={cancelEditing}
            loading={loading}
            sx={saveCancelButtonCommonStyles.sx}
            saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
            cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
          />
        </Stack>
      </StyledForm>
    </Box>
  );
};
